/**
 * API sözleşmesi tipleri.
 *
 * Kaynak: `openapi.yaml` (components/schemas) + `pkg/models/*.go`.
 * Alan adları camelCase ve birebir aynıdır; sözleşmede yapılan her değişiklik
 * bu dosyaya da yansıtılmalıdır. Tarih alanları JSON üzerinde RFC3339 string
 * olduğu için TypeScript tarafında `string` olarak taşınır.
 */

/* ------------------------------------------------------------------ */
/* Enum tipleri (openapi: components/schemas/*)                        */
/* ------------------------------------------------------------------ */

export type OSFamily = "windows" | "linux" | "snmp";

export type DeviceStatus = "online" | "offline" | "degraded" | "unknown";

export type Severity = "critical" | "high" | "medium" | "low" | "unknown";

export type Role = "admin" | "operator" | "viewer";

export type PatchStatus = "installed" | "pending" | "hidden" | "failed";

export type AlertState =
  | "firing"
  | "resolved"
  | "acknowledged"
  | "suppressed";

export type Comparison = "gt" | "gte" | "lt" | "lte";

export type MetricName =
  | "cpu.usage_percent"
  | "cpu.load1"
  | "memory.usage_percent"
  | "memory.used_bytes"
  | "swap.usage_percent"
  | "disk.usage_percent"
  | "disk.read_bytes"
  | "disk.write_bytes"
  | "net.rx_bytes"
  | "net.tx_bytes"
  | "system.uptime_seconds"
  | "system.process_count";

export const OS_FAMILIES: readonly OSFamily[] = ["windows", "linux", "snmp"];

export const DEVICE_STATUSES: readonly DeviceStatus[] = [
  "online",
  "offline",
  "degraded",
  "unknown",
];

export const SEVERITIES: readonly Severity[] = [
  "critical",
  "high",
  "medium",
  "low",
  "unknown",
];

export const ROLES: readonly Role[] = ["admin", "operator", "viewer"];

export const PATCH_STATUSES: readonly PatchStatus[] = [
  "installed",
  "pending",
  "hidden",
  "failed",
];

export const ALERT_STATES: readonly AlertState[] = [
  "firing",
  "resolved",
  "acknowledged",
  "suppressed",
];

export type DeviceEventType =
  | "alert"
  | "patch"
  | "inventory"
  | "enrollment"
  | "status";

export const COMPARISONS: readonly Comparison[] = ["gt", "gte", "lt", "lte"];

export const DEVICE_EVENT_TYPES: readonly DeviceEventType[] = [
  "alert",
  "patch",
  "inventory",
  "enrollment",
  "status",
];

export const METRIC_NAMES: readonly MetricName[] = [
  "cpu.usage_percent",
  "cpu.load1",
  "memory.usage_percent",
  "memory.used_bytes",
  "swap.usage_percent",
  "disk.usage_percent",
  "disk.read_bytes",
  "disk.write_bytes",
  "net.rx_bytes",
  "net.tx_bytes",
  "system.uptime_seconds",
  "system.process_count",
];

/* ------------------------------------------------------------------ */
/* Sayfalama                                                           */
/* ------------------------------------------------------------------ */

/** openapi: PageMeta */
export interface PageMeta {
  total: number;
  page: number;
  perPage: number;
}

/* ------------------------------------------------------------------ */
/* Cihazlar                                                            */
/* ------------------------------------------------------------------ */

/** openapi: Device / pkg/models.Device */
export interface Device {
  id: string;
  hostname: string;
  displayName?: string;
  osFamily: OSFamily;
  osName?: string;
  osVersion?: string;
  osBuild?: string;
  kernel?: string;
  agentVersion?: string;
  ips?: string[];
  macs?: string[];
  site?: string;
  tags?: string[];
  status: DeviceStatus;
  lastSeen: string;
  createdAt: string;
}

/** openapi: DeviceListResponse */
export interface DeviceListResponse {
  items: Device[];
  pageMeta: PageMeta;
}

/** openapi: DeviceUpdateRequest / pkg/models.DeviceUpdateRequest */
export interface DeviceUpdateRequest {
  displayName?: string;
  site?: string;
  tags?: string[];
}

/** GET /devices sorgu parametreleri (openapi: devices list parameters) */
export interface DeviceListQuery {
  page?: number;
  perPage?: number;
  q?: string;
  osFamily?: OSFamily;
  status?: DeviceStatus;
  site?: string;
  tag?: string;
  /** Sıralama alanı; azalan için '-' öneki (varsayılan "-lastSeen") */
  sort?: string;
}

/* ------------------------------------------------------------------ */
/* Metrikler                                                           */
/* ------------------------------------------------------------------ */

/** openapi: MetricSample / pkg/models.MetricSample */
export interface MetricSample {
  metric: MetricName;
  value: number;
  timestamp: string;
  labels?: Record<string, string>;
}

/** openapi: MetricBatch / pkg/models.MetricBatch */
export interface MetricBatch {
  deviceId: string;
  samples: MetricSample[];
}

/** openapi: MetricPoint / pkg/models.MetricPoint */
export interface MetricPoint {
  time: string;
  value: number;
}

/** openapi: MetricsQueryResponse / pkg/models.MetricsQueryResponse */
export interface MetricsQueryResponse {
  deviceId: string;
  metric: MetricName;
  from: string;
  to: string;
  intervalSeconds: number;
  points: MetricPoint[];
}

/** GET /devices/{id}/metrics sorgu parametreleri */
export interface MetricsQuery {
  metric: MetricName;
  from: string;
  to: string;
  intervalSeconds?: number;
  label?: string;
}

/* ------------------------------------------------------------------ */
/* Envanter / donanım                                                  */
/* ------------------------------------------------------------------ */

/** openapi: DiskInfo / pkg/models.DiskInfo */
export interface DiskInfo {
  mount: string;
  device?: string;
  fsType?: string;
  totalBytes?: number;
  usedBytes?: number;
}

/** openapi: NicInfo / pkg/models.NicInfo */
export interface NicInfo {
  name: string;
  mac?: string;
  ips?: string[];
  speedBps?: number;
  up: boolean;
}

/** openapi: HardwareInfo / pkg/models.HardwareInfo */
export interface HardwareInfo {
  manufacturer?: string;
  model?: string;
  serialNumber?: string;
  cpuModel?: string;
  cpuCores?: number;
  cpuLogical?: number;
  memoryBytes?: number;
  disks?: DiskInfo[];
  nics?: NicInfo[];
}

/** openapi: InventoryReport / pkg/models.InventoryReport */
export interface InventoryReport {
  deviceId: string;
  hostname: string;
  osFamily: OSFamily;
  osName?: string;
  osVersion?: string;
  osBuild?: string;
  kernel?: string;
  agentVersion: string;
  hardware: HardwareInfo;
  collectedAt: string;
}

/* ------------------------------------------------------------------ */
/* Patch                                                               */
/* ------------------------------------------------------------------ */

/** openapi: PatchRecord / pkg/models.PatchRecord */
export interface PatchRecord {
  id: string;
  source: string;
  identifier: string;
  title?: string;
  severity: Severity;
  status: PatchStatus;
  installedAt?: string;
  publishedAt?: string;
  rebootRequired: boolean;
}

/** openapi: PatchReport / pkg/models.PatchReport */
export interface PatchReport {
  deviceId: string;
  collectedAt: string;
  rebootRequired: boolean;
  records: PatchRecord[];
}

/** openapi: PatchSummaryItem / pkg/models.PatchSummaryItem */
export interface PatchSummaryItem {
  deviceId: string;
  hostname: string;
  pendingCount: number;
  criticalPendingCount: number;
  oldestPendingDays: number;
  lastCheckedAt?: string;
}

/** openapi: PatchSummaryResponse / pkg/models.PatchSummaryResponse */
export interface PatchSummaryResponse {
  items: PatchSummaryItem[];
  pageMeta: PageMeta;
}

/** GET /devices/{id}/patches sorgu parametreleri */
export interface DevicePatchQuery {
  status?: PatchStatus;
  page?: number;
  perPage?: number;
}

export interface PatchRecordListResponse {
  items: PatchRecord[];
  pageMeta: PageMeta;
}

/* ------------------------------------------------------------------ */
/* Uyarılar                                                            */
/* ------------------------------------------------------------------ */

/** openapi: AlertRule / pkg/models.AlertRule */
export interface AlertRule {
  id: string;
  name: string;
  metric: MetricName;
  comparison: Comparison;
  threshold: number;
  durationSeconds: number;
  severity: Severity;
  channels?: string[];
  deviceSelector?: Record<string, string>;
  enabled: boolean;
}

/** openapi: Alert / pkg/models.Alert */
export interface Alert {
  id: string;
  ruleId: string;
  deviceId: string;
  hostname: string;
  state: AlertState;
  severity: Severity;
  message: string;
  value: number;
  startedAt: string;
  resolvedAt?: string;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
}

/** GET /alerts sorgu parametreleri */
export interface AlertListQuery {
  state?: AlertState;
  severity?: Severity;
  deviceId?: string;
  page?: number;
  perPage?: number;
}

export interface AlertListResponse {
  items: Alert[];
  pageMeta: PageMeta;
}

/* ------------------------------------------------------------------ */
/* SNMP hedefleri                                                      */
/* ------------------------------------------------------------------ */

export type SnmpVersion = "v2c" | "v3";

/** openapi: SnmpTarget / pkg/models.SnmpTarget */
export interface SnmpTarget {
  id: string;
  ip: string;
  port: number;
  hostname?: string;
  sysName?: string;
  sysDescr?: string;
  vendor?: string;
  model?: string;
  version: SnmpVersion;
  pollIntervalSeconds: number;
  status: DeviceStatus;
  lastSeen?: string;
  createdAt: string;
}

/** openapi: SnmpTargetCreate / pkg/models.SnmpTargetCreate (credential alanları write-only) */
export interface SnmpTargetCreate {
  ip: string;
  port?: number;
  version: SnmpVersion;
  community?: string;
  username?: string;
  authProtocol?: string;
  authPassword?: string;
  privProtocol?: string;
  privPassword?: string;
  pollIntervalSeconds?: number;
}

export interface SnmpTargetListResponse {
  items: SnmpTarget[];
  pageMeta: PageMeta;
}

/* ------------------------------------------------------------------ */
/* Kullanıcılar / kimlik                                               */
/* ------------------------------------------------------------------ */

/** openapi: User / pkg/models.User */
export interface User {
  id: string;
  email: string;
  displayName?: string;
  role: Role;
  mfaEnabled: boolean;
  createdAt: string;
  lastLoginAt?: string;
}

/** openapi: LoginRequest / pkg/models.LoginRequest */
export interface LoginRequest {
  email: string;
  password: string;
}

/** openapi: LoginResponse / pkg/models.LoginResponse */
export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: User;
}

/** openapi: RefreshRequest / pkg/models.RefreshRequest */
export interface RefreshRequest {
  refreshToken: string;
}

/** openapi: POST /users isteği */
export interface UserCreateRequest {
  email: string;
  password: string;
  displayName?: string;
  role: Role;
}

/** openapi: PATCH /users/{id} isteği */
export interface UserUpdateRequest {
  displayName?: string;
  role?: Role;
  password?: string;
}

/* ------------------------------------------------------------------ */
/* Agent enroll (sözleşme bütünlüğü — panel bu uçları çağırmaz)        */
/* ------------------------------------------------------------------ */

/** openapi: EnrollRequest / pkg/models.EnrollRequest */
export interface EnrollRequest {
  token: string;
  /** PEM PKCS#10 CSR. Varsa sunucu yalnızca sertifikayı imzalar. */
  csrPem?: string;
  hostname: string;
  osFamily: OSFamily;
  agentVersion: string;
  site?: string;
  tags?: string[];
}

/** openapi: EnrollResponse / pkg/models.EnrollResponse */
export interface EnrollResponse {
  deviceId: string;
  certificatePem: string;
  /** Yalnızca sunucu anahtar çifti ürettiyse döner (CSR yoksa). Gizli bilgi. */
  privateKeyPem?: string;
  caChainPem: string;
  serverUrl: string;
  heartbeatSeconds: number;
}

/* ------------------------------------------------------------------ */
/* Enrollment tokenları (panel → agent kaydı)                          */
/* ------------------------------------------------------------------ */

/** openapi: EnrollmentTokenRequest / pkg/models.EnrollmentTokenRequest */
export interface EnrollmentTokenRequest {
  /** Geçerlilik (dakika); varsayılan 1440, en fazla 43200 (30 gün). */
  ttlMinutes?: number;
  site?: string;
  tags?: string[];
  note?: string;
}

/** openapi: EnrollmentTokenResponse / pkg/models.EnrollmentTokenResponse */
export interface EnrollmentTokenResponse {
  id: string;
  /** Gizli değer; yalnızca bir kez döner, sunucuda özeti saklanır. */
  token: string;
  expiresAt: string;
  site?: string;
  tags?: string[];
}

/* ------------------------------------------------------------------ */
/* Cihaz olay akışı                                                    */
/* ------------------------------------------------------------------ */

/** openapi: DeviceEvent / pkg/models.DeviceEvent */
export interface DeviceEvent {
  id: string;
  deviceId: string;
  type: DeviceEventType;
  severity?: Severity;
  message: string;
  at: string;
  refId?: string;
}

/** GET /devices/{id}/events sorgu parametreleri */
export interface DeviceEventQuery {
  type?: DeviceEventType;
  from?: string;
  to?: string;
  page?: number;
  perPage?: number;
}

/** openapi: GET /devices/{id}/events yanıtı (inline {items, pageMeta}) */
export interface DeviceEventListResponse {
  items: DeviceEvent[];
  pageMeta: PageMeta;
}

/* ------------------------------------------------------------------ */
/* Sağlık / hata                                                       */
/* ------------------------------------------------------------------ */

/** openapi: GET /health */
export interface HealthResponse {
  status: "ok";
  version?: string;
  uptimeSeconds?: number;
}

/** openapi: ErrorResponse / pkg/models.ErrorResponse */
export interface ErrorResponse {
  code: string;
  message: string;
  details?: Record<string, string>;
}